import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { ChatTranslations } from '../services/chat-translation.service';

interface Conversation {
  id: string | number;
  messages: any[];
  title?: string;
  isNew?: boolean;
  timestamp: Date;
  isLoaded: boolean
}

@Component({
  selector: 'wings-conversation-list',
  standalone: false,
  templateUrl: './wings-conversation-list.html',
  styleUrls: ['./wings-conversation-list.scss'],
  changeDetection: ChangeDetectionStrategy.Default // Usar detecção de mudanças padrão para garantir a atualização
})
export class WingsConversationListComponent implements OnChanges {
  @Input() conversations: Conversation[] = [];
  @Input() activeConversationId: string | number | null = null;
  @Input() isMinimized: boolean = false;
  @Input() hasEmptyConversation: boolean = false;
  @Input() isLoading: boolean = false; // Indicador global de carregamento
  @Input() loadingConversationIds: Set<string | number> = new Set(); // IDs das conversas em carregamento
  @Input() translations: ChatTranslations; // Propriedade de entrada para traduções

  @Output() conversationSelected = new EventEmitter<string | number>();
  @Output() newConversationRequested = new EventEmitter<void>();
  @Output() conversationDeleted = new EventEmitter<string | number>();

  // ID da conversa aguardando confirmação de exclusão
  public confirmingDeleteId: string | number | null = null;

  // Paginação
  public currentPage: number = 0;
  public readonly PAGE_SIZE: number = 6;

  constructor(private cdr: ChangeDetectorRef) {}

  /**
   * Retorna as conversas da página atual
   */
  get paginatedConversations(): Conversation[] {
    const start = this.currentPage * this.PAGE_SIZE;
    return this.conversations.slice(start, start + this.PAGE_SIZE);
  }

  /**
   * Retorna o total de páginas
   */
  get totalPages(): number {
    return Math.max(1, Math.ceil(this.conversations.length / this.PAGE_SIZE));
  }

  /**
   * Retorna o índice global da conversa baseado na página atual
   */
  getGlobalIndex(localIndex: number): number {
    return this.currentPage * this.PAGE_SIZE + localIndex;
  }

  /**
   * Avança para a próxima página
   */
  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.cdr.detectChanges();
    }
  }

  /**
   * Volta para a página anterior
   */
  previousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.cdr.detectChanges();
    }
  }

  /**
   * Navega para a página que contém a conversa com o ID informado
   */
  navigateToConversation(conversationId?: string | number): void {
    const id = conversationId != null ? conversationId : this.activeConversationId;
    if (id == null) return;
    const index = this.conversations.findIndex(c => c.id === id);
    if (index >= 0) {
      this.currentPage = Math.floor(index / this.PAGE_SIZE);
    }
  }
  
  /**
   * Solicita exclusão de uma conversa (mostra confirmação inline)
   */
  onRequestDelete(conversationId: string | number, event: Event): void {
    event.stopPropagation();
    this.confirmingDeleteId = conversationId;
    this.cdr.detectChanges();
  }

  /**
   * Confirma a exclusão da conversa.
   * Emite o evento para o componente pai, que faz o soft delete via backend.
   */
  onConfirmDelete(conversationId: string | number, event: Event): void {
    event.stopPropagation();
    
    // Resetar estado de confirmação
    this.confirmingDeleteId = null;
    
    // Emitir evento para o componente pai (que chamará o backend DELETE)
    this.conversationDeleted.emit(conversationId);
    
    // Forçar detecção de mudanças
    this.cdr.detectChanges();
  }

  /**
   * Cancela a exclusão da conversa
   */
  onCancelDelete(event: Event): void {
    event.stopPropagation();
    this.confirmingDeleteId = null;
    this.cdr.detectChanges();
  }
  
  // Implementação de OnChanges para detectar mudanças nas entradas
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.conversations || changes.hasEmptyConversation) {      
      // Ajustar página se necessário (ex: conversa deletada reduz total de páginas)
      if (this.currentPage >= this.totalPages) {
        this.currentPage = Math.max(0, this.totalPages - 1);
      }
      // Forçar detecção de mudanças quando as conversas ou o estado mudam
      this.cdr.detectChanges();
    }
    // Garantir que a conversa ativa esteja visível quando o ID ativo mudar
    if (changes.activeConversationId && this.activeConversationId != null) {
      this.navigateToConversation();
    }
  }

  onSelectConversation(conversationId: string | number): void {
    this.conversationSelected.emit(conversationId);
    // Garantir que a conversa selecionada esteja visível na paginação
    this.navigateToConversation(conversationId);
    // Forçar detecção de mudanças após a seleção
    this.cdr.detectChanges();
  }

  onCreateNewConversation(): void {
    // Se há conversas vazias, procurar e selecionar a primeira
    if (this.hasEmptyConversation) {
      // Encontrar a primeira conversa vazia
      const emptyConversation = this.conversations.find(conv => this.isEmptyConversation(conv));
      if (emptyConversation) {
        this.onSelectConversation(emptyConversation.id);
        return;
      }
    }
    // Solicitar a criação de uma nova conversa
    this.newConversationRequested.emit();
    // Navegar para a primeira página para ver a conversa nova
    this.currentPage = 0;
    // Forçar detecção de mudanças após requisitar nova conversa
    this.cdr.detectChanges();
  }

  getConversationNumber(index: number): number {
    return index + 1;
  }

  getConversationTitle(conv: Conversation, index: number): string {
    // Priorizar o título (preview do backend) que já contém o início da primeira mensagem
    if (conv.title) {
      return conv.title;
    }
    // Fallback para conteúdo da primeira mensagem carregada localmente
    if (conv.messages && conv.messages[0] && conv.messages[0].content) {
      return conv.messages[0].content;
    }
    return `Chat ${index + 1}`;
  }
  
  // Verifica se a conversa está vazia (só tem a mensagem de boas-vindas)
  isEmptyConversation(conv: Conversation): boolean {
    return conv.isNew;
  }

  // Verificar se uma conversa específica está sendo carregada
  isConversationLoading(conversationId: string | number): boolean {
    return this.loadingConversationIds.has(conversationId);
  }
  
  // Função trackBy para melhorar a performance de renderização da lista de conversas
  trackByConversationId(index: number, item: Conversation): string | number {
    return item.id; // Usar o ID como identificador único
  }
}